/**
 * Centralized error handling utility for API requests
 */

export class ApiError extends Error {
  constructor(message, originalError, userMessage) {
    super(message);
    this.name = "ApiError";
    this.originalError = originalError;
    this.userMessage = userMessage;
    this.status = originalError?.response?.status;
    this.shouldLog = false;
    this.shouldNotify = true;
  }
}

/**
 * Handles API errors consistently across the application
 * @param {Error} error - The original error from axios
 * @returns {ApiError} - Processed error with user-friendly message
 */
export function handleApiError(error) {
  let userMessage = "";
  let shouldLog = false;

  if (error.response?.status >= 500) {
    // Server errors - log for debugging
    shouldLog = true;
    userMessage = "Error interno del servidor. Por favor intenta de nuevo.";
  } else if (error.response?.status === 401) {
    // Unauthorized - specific handling
    userMessage = "Sesión expirada. Por favor inicia sesión de nuevo.";
  } else if (error.response?.status === 403) {
    // Forbidden
    userMessage = "No tienes permisos para realizar esta acción.";
  } else if (error.response?.status === 404) {
    // Not found
    userMessage = "Recurso no encontrado.";
  } else if (error.response?.status >= 400) {
    // Client errors - show API message if available
    userMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Datos inválidos. Por favor verifica la información.";
  } else if (error.code === "NETWORK_ERROR" || !error.response) {
    // Network errors
    userMessage =
      "Error de conexión. Por favor verifica tu internet e intenta de nuevo.";
  } else {
    // Unexpected errors - these should be logged
    shouldLog = true;
    userMessage = "Ocurrió un error inesperado. Por favor intenta de nuevo.";
  }

  const apiError = new ApiError(error.message, error, userMessage);
  apiError.shouldLog = shouldLog;

  // Log if necessary
  if (shouldLog && typeof window !== "undefined" && window.$log) {
    window.$log.error(error);
  }

  return apiError;
}

/**
 * Success response interceptor for Axios
 * @param {Object} response - The Axios response object
 * @returns {Object} The response object
 */
export const responseSuccessInterceptor = (response) => {
  // Just pass through successful responses
  return response;
};

/**
 * Error response interceptor for Axios
 * @param {Object} error - The Axios error object
 * @returns {Promise} A rejected promise with the formatted error
 */
export const responseErrorInterceptor = (error) => {
  // If the error does not have a response (e.g., network error),
  // just return the original error
  if (!error.response) {
    console.error("Network error or request cancelled:", error.message);
    return Promise.reject(error);
  }

  const { status, data } = error.response;

  // Log the error details
  console.error(`API Error [${status}]:`, data);

  // You can add custom handling for specific status codes here
  if (status === 401) {
    // Handle unauthorized access (e.g., redirect to login)
    console.error("Authentication failed, please login again");
    // Potential implementation: logout user, redirect to login, etc.
  } else if (status === 403) {
    console.error("Access forbidden");
  } else if (status === 409) {
    console.error("Resource conflict", data);
  } else if (status === 500) {
    console.error("Server error", data);
  }

  // Return the error to be handled by the calling code
  return Promise.reject(error);
};
